import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Button, Container, Alert, InputGroup, Row, Col } from 'react-bootstrap'
import { userAPI } from '../api/userAPI'
import { permissionsAPI } from '../api/permissionsAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons'

function UserCreateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [passwordConfirm, setPasswordConfirm] = useState('')
    const [user, setUser] = useState({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        is_active: true,
        password: ''
    })
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [makePermissionAdmin, setMakePermissionAdmin] = useState(false)

    const submitHandler = async (e) => {
        e.preventDefault()
        if (user.password !== passwordConfirm) {
            setError('Passwords do not match')
            return
        }
        try {
            // Include is_permission_admin flag in the user data
            const userData = {
                ...user,
                is_permission_admin: makePermissionAdmin
            }
            await userAPI.create(userData)
            navigate('/users')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('users.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to create users.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <h1>Create User</h1>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="username">
                            <Form.Label>Username*</Form.Label>
                            <Form.Control
                                type="text"
                                value={user.username}
                                onChange={(e) => setUser({...user, username: e.target.value})}
                                required
                                placeholder="Enter username"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="password">
                            <Form.Label>Password*</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    value={user.password}
                                    onChange={(e) => setUser({...user, password: e.target.value})}
                                    required
                                    placeholder="Enter password"
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <Button 
                                    variant="outline-secondary"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="confirm_password">
                            <Form.Label>Confirm Password*</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={passwordConfirm}
                                    onChange={(e) => setPasswordConfirm(e.target.value)}
                                    required
                                    placeholder="Confirm password"
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <Button 
                                    variant="outline-secondary"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="first_name">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={user.first_name}
                                onChange={(e) => setUser({...user, first_name: e.target.value})}
                                placeholder="Enter first name"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="last_name">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={user.last_name}
                                onChange={(e) => setUser({...user, last_name: e.target.value})}
                                placeholder="Enter last name"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="email">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={user.email}
                                onChange={(e) => setUser({...user, email: e.target.value})}
                                placeholder="Enter email"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="permissionAdmin">
                            <Form.Label>Permission Admin (can manage other users&apos; permissions)</Form.Label>
                            <Form.Check
                                type="switch"
                                id="makePermissionAdmin"
                                checked={makePermissionAdmin}
                                onChange={(e) => setMakePermissionAdmin(e.target.checked)}
                            />
                        </Form.Group>

                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Button variant="success" type="submit" className="me-2">
                            Create
                        </Button>
                        <Button 
                            variant="outline-primary" 
                            onClick={() => navigate('/users')}
                        >
                            <i className="fas fa-arrow-left me-2"></i>
                            Back to List
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default UserCreateScreen 